import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../utils/api';            // your Axios instance (baseURL + interceptor)

const API_BASE =  process.env.REACT_APP_API_URL;    // for image previews only

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [imageFiles, setImageFiles] = useState([]);          // newly selected files
  const [existingImageUrls, setExistingImageUrls] = useState([]); // already saved URLs
  const [uploadedUrls, setUploadedUrls] = useState([]);      // URLs from the current upload session
  const [variants, setVariants] = useState([{ size: 'M', color: '', stock: '10', price: '' }]);
  const [releaseDate, setReleaseDate] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  // ----- Fetch products & categories (with cache-buster) -----
  const fetchProducts = () => {
    api.get(`/api/admin/products?_=${Date.now()}`)
      .then(res => setProducts(res.data))
      .catch(() => setError('Failed to load products'))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    api.get(`/api/categories?_=${Date.now()}`)
      .then(res => {
        const valid = res.data.filter(cat => cat.name);
        setCategories(valid);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // ----- Reset form -----
  const resetForm = () => {
    setName('');
    setDescription('');
    setCategoryId('');
    setBasePrice('');
    setImageFiles([]);
    setExistingImageUrls([]);
    setUploadedUrls([]);
    setVariants([{ size: 'M', color: '', stock: '10', price: '' }]);
    setReleaseDate('');
    setEditingProduct(null);
    setShowForm(false);
    setError('');
  };

  // ----- Edit product -----
  const handleEdit = (product) => {
    setName(product.name);
    setDescription(product.description || '');
    setCategoryId(product.categoryId);
    setBasePrice(product.basePrice.toString());
    setExistingImageUrls(product.imageUrls || []);
    setUploadedUrls([]);
    setVariants(
      product.variants?.length
        ? product.variants.map(v => ({
            size: v.size,
            color: v.color || '',
            stock: v.stock.toString(),
            price: v.price ? v.price.toString() : '',
          }))
        : [{ size: 'M', color: '', stock: '10', price: '' }]
    );
    setReleaseDate(product.releaseDate ? product.releaseDate.substring(0, 16) : '');
    setEditingProduct(product);
    setShowForm(true);
  };

  // ----- Upload a single image to Cloudinary -----
  const handleImageUpload = async (file) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Session expired. Please log in again.');
      throw new Error('No token');
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${API_BASE}/api/admin/products/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response.data.url;   // { url: "https://cloudinary.com/..." }
  };

  // ----- Handle multiple file selection & upload -----
  const handleFilesSelected = async (files) => {
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const url = await handleImageUpload(file);
        newUrls.push(url);
      } catch (err) {
        alert('Image upload failed. Please log in and try again.');
        setUploading(false);
        return;
      }
    }
    setUploadedUrls(prev => [...prev, ...newUrls]);   // show previews
    setUploading(false);
  };

  // ----- Remove a preview URL (doesn't affect already saved images) -----
  const removeUploadedUrl = (index) => {
    setUploadedUrls(prev => prev.filter((_, i) => i !== index));
  };

  // ----- Remove an existing image URL (when editing) -----
  const removeExistingImage = (index) => {
    setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  // ----- Submit (add / edit) -----
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Combine existing (if editing) + newly uploaded URLs
    const finalImageUrls = editingProduct
      ? [...existingImageUrls, ...uploadedUrls]
      : [...uploadedUrls];

    const productData = {
      name,
      description,
      categoryId,
      basePrice: parseFloat(basePrice),
      imageUrls: finalImageUrls,
      variants: variants.map(v => ({
        size: v.size,
        color: v.color,
        stock: parseInt(v.stock),
        price: v.price ? parseFloat(v.price) : parseFloat(basePrice),
      })),
      releaseDate: releaseDate || null,
    };

    try {
      if (editingProduct) {
        await api.put(`/api/admin/products/${editingProduct.id}`, productData);
        alert('Product updated!');
      } else {
        await api.post('/api/admin/products', productData);
        alert('Product added!');
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      setError('Failed to save product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ----- Soft delete -----
  const handleSoftDelete = async (id) => {
    if (!window.confirm('Move to trash?')) return;
    await api.put(`/api/admin/products/${id}/delete`);
    fetchProducts();
  };

  // ----- Variant helpers -----
  const addVariant = () =>
    setVariants([...variants, { size: 'M', color: '', stock: '10', price: '' }]);
  const updateVariant = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setVariants(newVariants);
  };
  const removeVariant = (index) =>
    setVariants(variants.filter((_, i) => i !== index));

  // ----- Get image preview URL -----
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/uploads/')) return `${API_BASE}/images/${url.replace('/uploads/', '')}`;
    return url;
  };

  // ====================== UI ======================
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Products</h2>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-black text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-gray-800 transition"
        >
          Add Product
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 mb-8 space-y-5 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Product Name *"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            <select
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none"
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <textarea
            placeholder="Description"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="number"
              placeholder="Base Price (₦)"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none"
              value={basePrice}
              onChange={e => setBasePrice(e.target.value)}
              required
            />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Drop Date (optional)</label>
              <input
                type="datetime-local"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none"
                value={releaseDate}
                onChange={e => setReleaseDate(e.target.value)}
              />
            </div>
          </div>

          {/* ====== IMAGE UPLOAD SECTION ====== */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Product Images</p>

            {/* Existing images (when editing) */}
            {existingImageUrls.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {existingImageUrls.map((url, idx) => (
                  <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Newly uploaded images (previews) */}
            {uploadedUrls.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {uploadedUrls.map((url, idx) => (
                  <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeUploadedUrl(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* File input */}
            <div className="flex items-center gap-3">
              <label className="cursor-pointer bg-black text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-800 transition">
                {uploading ? 'Uploading…' : 'Choose Images'}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={e => handleFilesSelected(e.target.files)}
                  disabled={uploading}
                />
              </label>
              {uploading && (
                <span className="text-sm text-gray-500">Uploading images to Cloudinary…</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Images are uploaded to Cloudinary and will be permanently accessible in emails.
            </p>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Variants</h4>
              <button type="button" onClick={addVariant} className="text-sm text-blue-600 hover:underline">
                + Add Variant
              </button>
            </div>
            {variants.map((v, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2 items-center">
                <select
                  value={v.size}
                  onChange={e => updateVariant(i, 'size', e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-2 text-sm"
                >
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  value={v.color}
                  onChange={e => updateVariant(i, 'color', e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-2 text-sm"
                >
                  <option value="">No colour</option>
                  {['Black', 'White', 'Red', 'Blue', 'Green', 'Grey'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  type="number"
                  placeholder="Stock"
                  className="border border-gray-200 rounded-lg px-2 py-2 text-sm w-20"
                  value={v.stock}
                  onChange={e => updateVariant(i, 'stock', e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Price (optional)"
                  className="border border-gray-200 rounded-lg px-2 py-2 text-sm w-28"
                  value={v.price}
                  onChange={e => updateVariant(i, 'price', e.target.value)}
                />
                {variants.length > 1 && (
                  <button type="button" onClick={() => removeVariant(i)} className="text-red-500 text-xs">
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="bg-green-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingProduct ? 'Update Product' : 'Save Product'}
            </button>
            <button type="button" onClick={resetForm} className="bg-gray-200 text-gray-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-gray-300 transition">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Products List */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading products…</div>
      ) : products.length === 0 ? (
        <div className="text-center py-10 bg-white border border-gray-200 rounded-2xl text-gray-500">
          No products found.
        </div>
      ) : (
        <div className="space-y-2">
          {products.map(product => (
            <div key={product.id} className="flex justify-between items-center bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition">
              <div className="flex items-center gap-4">
                {product.imageUrls?.[0] && (
                  <img
                    src={getImageUrl(product.imageUrls[0])}
                    alt=""
                    className="w-12 h-12 object-cover rounded-lg border"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-500">₦{product.basePrice?.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => handleEdit(product)} className="text-blue-600 hover:underline text-sm">
                  Edit
                </button>
                <button onClick={() => handleSoftDelete(product.id)} className="text-red-500 hover:underline text-sm">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}